import { type StudyRunCaseDashboardStageHeatmapRowDto, type StudyRunCaseDashboardSummaryDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { type MeiaScoringConfigFormValues } from '@schemas/meia.schema';
import { type FC } from 'react';
import {
  ColumnLabel,
  DashboardStatTile,
  InfoTooltip,
  LegendSwatch,
  ScorePill,
  VerdictDot,
} from './meia-p4-dashboard-primitives.component';
import { formatDashboardScore, scoreFromBand } from './meia-p4-run-cases.util';

type ThreadSummaryMetricsProps = {
  summary: StudyRunCaseDashboardSummaryDto;
};

const ThreadSummaryMetrics: FC<ThreadSummaryMetricsProps> = (props) => {
  const { summary } = props;

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
      <DashboardStatTile
        band={summary.threadBand}
        label="Thread Verdict"
        value={`${formatDashboardScore(summary.threadScore ?? scoreFromBand(summary.threadBand))} / 100`}
      />
      <DashboardStatTile
        label="Stages Green"
        numerator={summary.stagesGreen}
        progressTone="green"
        total={summary.stagesTotal}
        value={`${summary.stagesGreen} / ${summary.stagesTotal}`}
      />
      <DashboardStatTile
        label="Stages Yellow"
        numerator={summary.stagesYellow}
        progressTone="yellow"
        total={summary.stagesTotal}
        value={`${summary.stagesYellow} / ${summary.stagesTotal}`}
      />
      <DashboardStatTile
        label="Stages Red"
        numerator={summary.stagesRed}
        progressTone="red"
        total={summary.stagesTotal}
        value={`${summary.stagesRed} / ${summary.stagesTotal}`}
      />
    </div>
  );
};

type StageHeatmapProps = {
  rows: StudyRunCaseDashboardStageHeatmapRowDto[];
  scoringConfig: MeiaScoringConfigFormValues;
  summary: StudyRunCaseDashboardSummaryDto;
};

export const StageHeatmap: FC<StageHeatmapProps> = (props) => {
  const { rows, scoringConfig, summary } = props;
  const greenLabel = `≥${scoringConfig.greenThreshold}`;
  const yellowLabel = `${scoringConfig.yellowThreshold}–${scoringConfig.greenThreshold - 1}`;
  const redLabel = `<${scoringConfig.yellowThreshold}`;

  return (
    <Tile
      className="overflow-hidden"
      contentClassName="gap-3"
      header={{
        actions: (
          <div className="flex items-center gap-3 font-mono text-[10px] text-gray-500">
            <LegendSwatch
              colorClass="bg-green-600"
              label={greenLabel}
            />
            <LegendSwatch
              colorClass="bg-yellow-500"
              label={yellowLabel}
            />
            <LegendSwatch
              colorClass="bg-red-600"
              label={redLabel}
            />
            <LegendSwatch
              colorClass="bg-gray-200"
              label="—"
            />
          </div>
        ),
        title: (
          <span className="inline-flex items-center gap-1.5">
            Stage × Measure Heatmap
            <InfoTooltip>
              Scores are normalized to 0-100 from stored scoring hierarchy values. A stage shows — only when it has no
              scored task or measure data.
            </InfoTooltip>
          </span>
        ),
      }}
    >
      <ThreadSummaryMetrics summary={summary} />
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-left text-xs">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="px-3 py-2 font-medium text-gray-700">Stage</th>
              <th className="px-3 py-2 text-center font-medium text-green-700">
                <ColumnLabel tooltip="Measure of Success rollup score for measures linked to this stage.">
                  MoS
                </ColumnLabel>
              </th>
              <th className="px-3 py-2 text-center font-medium text-orange-600">
                <ColumnLabel tooltip="Measure of Effectiveness rollup score for stage-linked task effects.">
                  MoE
                </ColumnLabel>
              </th>
              <th className="px-3 py-2 text-center font-medium text-blue-700">
                <ColumnLabel tooltip="Measure of Performance score from matched MoP hierarchy data, falling back to real scored task data when the stored output lacks per-MoP detail.">
                  MoP
                </ColumnLabel>
              </th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">
                <ColumnLabel tooltip="Stage composite is calculated from scored tasks, or linked measure scores when task scores are unavailable.">
                  Composite
                </ColumnLabel>
              </th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Tasks</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                className="border-b border-gray-100 last:border-0"
                key={row.stageId}
              >
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-950">{row.stageName}</div>
                  <div className="font-mono text-[10px] text-gray-400">{row.stageId.slice(0, 8)}</div>
                </td>
                <td className="px-3 py-2 text-center">
                  <ScorePill
                    score={row.mosScore}
                    scoringConfig={scoringConfig}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <ScorePill
                    score={row.moeScore}
                    scoringConfig={scoringConfig}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <ScorePill
                    score={row.mopScore}
                    scoringConfig={scoringConfig}
                  />
                </td>
                <td className="px-3 py-2 text-center font-semibold text-gray-950">
                  {formatDashboardScore(row.compositeScore)}
                </td>
                <td className="px-3 py-2 text-center font-mono text-gray-500">{row.taskCount} task</td>
                <td className="px-3 py-2 text-center">
                  <VerdictDot band={row.verdictBand} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Tile>
  );
};
